import { Component, HostListener, inject, Inject, OnInit, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SharedModule } from './shared/shared.module';
import { MatSidenav } from '@angular/material/sidenav';
import { SpinnerComponent } from './common/spinner/spinner.component';
import { isPlatformBrowser } from '@angular/common';
import { SideNavComponent } from './side-nav/side-nav.component';
import { SideNavService } from './side-nav/side-nav.service';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import * as UserActions from './app-store/user/user.actions';
import { Convert, UserModel } from './models/user-model';
import { UserService } from './services/user.service';
import { UserState } from './app-store/user/user.state';
import { SeoService } from './services/seo.service';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: true,
    imports: [RouterOutlet, SharedModule, SideNavComponent, SpinnerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly indexableRoutes = new Set<string>([
    '/',
    '/index',
    '/about',
    '/featured-pets',
    '/terms-and-conditions',
    '/privacy-policy',
    '/refund-and-cancellation',
    '/return-policy',
    '/shipping-policy',
    '/contact-us'
  ]);
  
  isUserLoggedIn = false;
  isAdmin = false;
  title = 'adopals'; // Reverted title
  @ViewChild('sidenav') public sidenav: MatSidenav | any;
  isMobileScreen = false;
  userModel: UserModel = Convert.getDefaultUserModel();


  constructor(
    private sideNavService: SideNavService,
    private store: Store<{ user: UserState }>,
    @Inject(PLATFORM_ID) private platformId: Object,
    private userService: UserService,
    private router: Router,
    private auth: Auth,
    private seoService: SeoService
    
  ) {
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        let res = await this.userService.getUserById(user.uid);
        this.userModel = Convert.toUserModel(JSON.stringify(res));
        this.userModel.isLoggedIn = true;
        this.userService.setCurrentUser(this.userModel);
        this.store.dispatch(UserActions.loginSuccess({ user: this.userModel }));
        // Update admin state if user is admin
        const role = this.userService.getCurrentUserRole();
        this.isAdmin = role === 'admin';
        this.isUserLoggedIn = this.isAdmin || this.isUserLoggedIn;
        this.updateLayoutClasses();
        if (this.isAdmin && this.sidenav) {
          this.sidenav.open();
        }
      } else {
        // User logged out
        this.isAdmin = false;
        this.isUserLoggedIn = false;
        this.updateLayoutClasses();
        if (this.sidenav) {
          this.sidenav.close();
        }
      }
      this.markE2EReady();
    });

    // Listen for user role changes (e.g., switch role, logout)
    this.userService.currentUserRole$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(role => {
      this.isAdmin = role === 'admin';
      this.updateLayoutClasses();
      if (this.isAdmin) {
        this.isUserLoggedIn = true;
        if (this.sidenav) {
          this.sidenav.open();
        }
      } else {
        this.isUserLoggedIn = false;
        if (this.sidenav) {
          this.sidenav.close();
        }
      }
    });
  }
  ngOnInit() {
    this.markE2EReady();
    // Check if user is admin
    const role = this.userService.getCurrentUserRole();
    this.isAdmin = role === 'admin';
    this.isUserLoggedIn = this.isAdmin || this.isUserLoggedIn;
    this.updateLayoutClasses();

    this.applyRouteSeo(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.applyRouteSeo(event.urlAfterRedirects);
    });
  }

  private applyRouteSeo(url: string) {
    const path = this.normalizePath(url);
    const canonical = `https://adopals.com${path === '/' ? '' : path}`;

    this.seoService.setCanonicalUrl(canonical);
    this.seoService.setMetaRobots(
      this.indexableRoutes.has(path) ? 'index, follow' : 'noindex, nofollow'
    );
  }

  private normalizePath(url: string): string {
    const [pathWithoutQuery] = url.split('?');
    const [pathWithoutHash] = pathWithoutQuery.split('#');
    return pathWithoutHash || '/';
  }

  private markE2EReady(): void {
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-e2e-ready', 'true');
      (window as Window & { __E2E_READY__?: boolean }).__E2E_READY__ = true;
    }
  }

  updateLayoutClasses() {
    if (typeof window !== 'undefined' && window.document) {
      // Handle admin layout
      if (this.isAdmin) {
        document.body.classList.add('admin-layout');
      } else {
        document.body.classList.remove('admin-layout');
      }
    }
  }

  ngAfterViewInit() {
    this.sideNavService.setSidenav(this.sidenav);
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileScreen = window.innerWidth < 768;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (isPlatformBrowser(this.platformId)) {
      const width = (event.target as Window | null)?.innerWidth ?? window.innerWidth;
      if (width < 768) {
        this.isMobileScreen = true;
      } else {
        this.isMobileScreen = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle Google Sign-in popup events
   */
  onSignInAttempt(method: string): void {
    
    // You can add analytics tracking here
    // gtag('event', 'sign_in_popup_attempt', { method: method });
  }

  onPopupClosed(): void {
    
    // You can add analytics tracking here
    // gtag('event', 'sign_in_popup_closed');
  }
}